import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EChart from "../../../../components/Dashboard/Graphic/charts/EChart";
import { PivotGrid } from "../../../../components/Exports/Builder/components/PivotGrid";
import IndicatorCard from "../../../../components/Dashboard/Static/IndicatorCard";
import Icon from "../../../../components/Icon/Icon";
import { deserializePivot } from "../utils/pivotSnapshot";
import { dropZeroPoints } from "../../../../components/Exports/Builder/utils/pivotToChartData";

/**
 * Paints a single frozen piece from its stored `result`. No EQL, no queries —
 * every piece renders from the snapshot captured when it was added/refreshed.
 * Reuses the very components the Builder and Static dashboard use, so a frozen
 * piece is visually identical to its live source.
 */
export const RenderPiece = ({ piece, showTitle = true }) => {
  const [t] = useTranslation("exports");
  const { kind, title, result } = piece || {};
  const isChart = kind !== "indicator" && kind !== "table";
  // Also filtered here, not only on freeze: pieces saved before the rule still
  // carry their zero points until refreshed.
  const chartPoints = isChart ? dropZeroPoints(result?.data) : [];
  const emptyChart = isChart && !chartPoints.length && !result?.rings?.length;

  if (!result || emptyChart) {
    return (
      <Box
        className="dyTheme1 dyBorder1 rounded"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 2,
          color: "text.secondary",
        }}
      >
        <Icon name="bold_format_search" size={32} />
        <Typography variant="body2">
          {t("bi.empty_piece", "Sin datos")}
        </Typography>
      </Box>
    );
  }

  if (kind === "indicator") {
    return (
      <IndicatorCard
        label={showTitle ? title || result.label : undefined}
        value={
          typeof result.value === "number"
            ? result.value.toLocaleString()
            : result.value
        }
        iconName={piece.iconName || "bold_chart_square"}
        tone={piece.tone}
        accentColor={piece.accentColor}
      />
    );
  }

  if (kind === "table") {
    return (
      <Box
        className="dyTheme1 dyBorder1 rounded"
        sx={{ height: "100%", p: 1, display: "flex", flexDirection: "column" }}
      >
        {showTitle && title && (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 0.5, px: 0.5 }}
            noWrap
          >
            {title}
          </Typography>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {/* bare: this Box is already the piece's card; PivotGrid's own card
              drew a second border and margin under the title. */}
          <PivotGrid
            bare
            pivot={deserializePivot(result.pivot)}
            fieldTitle={(f) => result.fieldTitles?.[f] || f}
            valueMode={result.valueMode}
          />
        </Box>
      </Box>
    );
  }

  // chart
  return (
    <Box
      className="dyTheme1 dyBorder1 rounded"
      sx={{ height: "100%", p: 1.5, display: "flex", flexDirection: "column" }}
    >
      {showTitle && title && (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 0.5, px: 0.5 }}
          noWrap
        >
          {title}
        </Typography>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <EChart
          chartData={{
            id: piece.id,
            title,
            type: "pivot",
            data: chartPoints,
            rings: result.rings,
          }}
          type={result.type}
          title={showTitle ? title : undefined}
          height="100%"
          animation={false}
        />
      </Box>
    </Box>
  );
};

export default RenderPiece;
